import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { uploadMany } from "./files"
import { sdk } from "./sdk"
import { qk } from "./api"
import { adjustInventory } from "./inventory"

/**
 * Catalog writes on Medusa's native admin API (the backend's staff-access
 * middleware gates them by area: products:write, inventory:write).
 *
 * The editor works on one plain ProductDraft; saveProduct() turns it into the
 * right sequence of Medusa calls (product, options, variant batch, stock).
 */

export const CURRENCY = "usd"

export type DraftImage = { id?: string; url: string }
export type DraftOption = { id?: string; title: string; values: string[]; /** value → Medusa value id */ value_ids?: Record<string, string> }
export type DraftVariant = {
  id?: string
  /** option title → value */
  options: Record<string, string>
  sku: string
  barcode: string
  price: string
  compare_at: string
  /** Cost per item (variant.metadata.cost_per_item): for profit reports, never shown to customers */
  cost: string
  weight: string
  manage_inventory: boolean
  allow_backorder: boolean
  /** stocked quantity at the default location; "" = leave unchanged */
  quantity: string
  /** for change detection */
  original_quantity?: number | null
  inventory_item_id?: string
}

export type ProductDraft = {
  title: string
  handle: string
  subtitle: string
  description: string
  status: "draft" | "published" | "proposed" | "rejected"
  archived: boolean
  collection_id: string
  type: string
  tags: string[]
  vendor: string
  seo_title: string
  seo_description: string
  images: DraftImage[]
  thumbnail: string
  options: DraftOption[]
  variants: DraftVariant[]
  /** metadata we don't own, kept as-is on save */
  extra_metadata: Record<string, unknown>
  /** Medusa shipping profile; every product needs one to be shippable */
  shipping_profile_id?: string
}

export const DEFAULT_OPTION = "Title"
export const DEFAULT_VALUE = "Default Title"

export function emptyVariant(options: Record<string, string> = { [DEFAULT_OPTION]: DEFAULT_VALUE }): DraftVariant {
  return { options, sku: "", barcode: "", price: "", compare_at: "", cost: "", weight: "", manage_inventory: true, allow_backorder: false, quantity: "0" }
}

export function emptyDraft(): ProductDraft {
  return {
    title: "",
    handle: "",
    subtitle: "",
    description: "",
    status: "draft",
    archived: false,
    collection_id: "",
    type: "",
    tags: [],
    vendor: "",
    seo_title: "",
    seo_description: "",
    images: [],
    thumbnail: "",
    options: [],
    variants: [emptyVariant()],
    extra_metadata: {},
  }
}

/** Shopify-style: a product with no real options has one hidden "Title / Default Title" option. */
export const hasRealOptions = (d: ProductDraft) =>
  d.options.length > 0 && !(d.options.length === 1 && d.options[0].title === DEFAULT_OPTION && d.options[0].values.length === 1 && d.options[0].values[0] === DEFAULT_VALUE)

export const variantTitle = (v: DraftVariant, d: ProductDraft) =>
  hasRealOptions(d) ? d.options.map((o) => v.options[o.title]).filter(Boolean).join(" / ") : DEFAULT_VALUE

export function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120)
}

/** Every combination of option values, keeping any matching existing variant's data. */
export function regenerateVariants(options: DraftOption[], current: DraftVariant[]): DraftVariant[] {
  const usable = options.filter((o) => o.title.trim() && o.values.length)
  if (!usable.length) {
    const keep = current.find((v) => v.options[DEFAULT_OPTION] === DEFAULT_VALUE) ?? current[0]
    return [{ ...(keep ?? emptyVariant()), options: { [DEFAULT_OPTION]: DEFAULT_VALUE } }]
  }
  let combos: Record<string, string>[] = [{}]
  for (const o of usable) combos = combos.flatMap((c) => o.values.map((v) => ({ ...c, [o.title]: v })))
  const template = current[0]
  // Going from the hidden default option to real options: the old default
  // variant becomes the first combination (keeps its id and inventory item).
  const defaultVariant = current.length === 1 && current[0].options[DEFAULT_OPTION] === DEFAULT_VALUE ? current[0] : undefined
  return combos.map((c, i) => {
    const match = current.find((v) => usable.every((o) => v.options[o.title] === c[o.title]))
    if (match) return { ...match, options: c }
    if (i === 0 && defaultVariant) return { ...defaultVariant, options: c }
    // A new combination: copy price etc. from the first variant, like Shopify does.
    const base = template ?? emptyVariant()
    return { ...emptyVariant(c), price: base.price, compare_at: base.compare_at, cost: base.cost, weight: base.weight, manage_inventory: base.manage_inventory }
  })
}

// ── Medusa ⇄ draft ─────────────────────────────────────────────────────────

export const PRODUCT_FIELDS =
  "*shipping_profile,*variants,*variants.prices,*variants.options,*variants.options.option,*variants.inventory_items,*variants.inventory_items.inventory,*variants.inventory_items.inventory.location_levels,*images,*options,*options.values,*tags,*type,*collection,*sales_channels,+metadata"

const num = (s: string) => (s.trim() === "" ? null : Number(s))
/** Amounts shown with cents like Shopify (19.00, not 19). */
const money2 = (v: unknown) => (v == null || v === "" ? "" : Number.isFinite(Number(v)) ? Number(v).toFixed(2) : String(v))
const str = (n: unknown) => (n == null ? "" : String(n))

export function productToDraft(p: any, locationId?: string): ProductDraft {
  const meta = { ...(p.metadata ?? {}) } as Record<string, unknown>
  const vendor = str(meta.vendor)
  const seo_title = str(meta.seo_title)
  const seo_description = str(meta.seo_description)
  delete meta.vendor
  delete meta.seo_title
  delete meta.seo_description
  delete meta.archived
  const options: DraftOption[] = (p.options ?? []).map((o: any) => ({
    id: o.id,
    title: o.title,
    values: (o.values ?? []).map((v: any) => v.value),
    value_ids: Object.fromEntries((o.values ?? []).map((v: any) => [v.value, v.id])),
  }))
  const variants: DraftVariant[] = (p.variants ?? []).map((v: any) => {
    const opt: Record<string, string> = {}
    for (const ov of v.options ?? []) {
      const title = ov.option?.title ?? options.find((o) => o.id === ov.option_id)?.title
      if (title) opt[title] = ov.value
    }
    const link = v.inventory_items?.[0]
    const levels = link?.inventory?.location_levels ?? []
    const level = levels.find((l: any) => l.location_id === locationId) ?? levels[0]
    const qty = level ? Number(level.stocked_quantity) : null
    return {
      id: v.id,
      options: opt,
      sku: str(v.sku),
      barcode: str(v.barcode ?? v.ean ?? v.upc),
      price: money2(v.prices?.find((x: any) => x.currency_code === CURRENCY && !x.price_list_id && !x.rules_count && !Object.keys(x.rules ?? {}).length)?.amount ?? v.prices?.find((x: any) => x.currency_code === CURRENCY)?.amount),
      compare_at: money2(v.metadata?.compare_at_price),
      cost: money2(v.metadata?.cost_per_item),
      weight: str(v.weight),
      manage_inventory: !!v.manage_inventory,
      allow_backorder: !!v.allow_backorder,
      quantity: qty == null ? "" : String(qty),
      original_quantity: qty,
      inventory_item_id: link?.inventory_item_id ?? link?.inventory?.id,
    }
  })
  return {
    title: p.title ?? "",
    handle: p.handle ?? "",
    subtitle: p.subtitle ?? "",
    description: p.description ?? "",
    status: p.status,
    archived: (p.metadata ?? {}).archived === true,
    collection_id: p.collection_id ?? "",
    type: p.type?.value ?? "",
    tags: (p.tags ?? []).map((t: any) => t.value),
    vendor,
    seo_title,
    seo_description,
    images: (p.images ?? []).map((i: any) => ({ id: i.id, url: i.url })),
    thumbnail: p.thumbnail ?? "",
    options,
    variants,
    extra_metadata: meta,
    shipping_profile_id: p.shipping_profile?.id ?? undefined,
  }
}

async function ensureType(value: string): Promise<string | null> {
  const v = value.trim()
  if (!v) return null
  const { product_types } = await sdk.admin.productType.list({ value: v, limit: 1 } as any)
  const hit = product_types.find((t: any) => t.value.toLowerCase() === v.toLowerCase())
  if (hit) return hit.id
  const { product_type } = await sdk.admin.productType.create({ value: v })
  return product_type.id
}

async function ensureTags(values: string[]): Promise<{ id: string }[]> {
  const out: { id: string }[] = []
  for (const raw of values) {
    const v = raw.trim()
    if (!v) continue
    const { product_tags } = await sdk.admin.productTag.list({ value: v, limit: 1 } as any)
    const hit = product_tags.find((t: any) => t.value.toLowerCase() === v.toLowerCase())
    out.push({ id: hit ? hit.id : (await sdk.admin.productTag.create({ value: v })).product_tag.id })
  }
  return out
}

function variantPayload(v: DraftVariant, d: ProductDraft) {
  const real = hasRealOptions(d)
  const price = num(v.price)
  const compare = num(v.compare_at)
  return {
    title: variantTitle(v, d),
    sku: v.sku.trim() || null,
    barcode: v.barcode.trim() || null,
    weight: num(v.weight),
    manage_inventory: v.manage_inventory,
    allow_backorder: v.allow_backorder,
    options: real ? Object.fromEntries(d.options.map((o) => [o.title, v.options[o.title]])) : { [DEFAULT_OPTION]: DEFAULT_VALUE },
    prices: price == null ? [] : [{ currency_code: CURRENCY, amount: price }],
    metadata: { compare_at_price: compare, cost_per_item: num(v.cost) },
  }
}

function productPayload(d: ProductDraft, typeId: string | null, tags: { id: string }[]) {
  const thumb = d.thumbnail && d.images.some((i) => i.url === d.thumbnail) ? d.thumbnail : d.images[0]?.url ?? null
  return {
    title: d.title.trim(),
    handle: d.handle.trim() || slugify(d.title),
    subtitle: d.subtitle.trim() || null,
    description: d.description,
    status: d.archived ? "draft" : d.status,
    collection_id: d.collection_id || null,
    type_id: typeId,
    tags,
    images: d.images.map((i) => ({ url: i.url })),
    thumbnail: thumb,
    metadata: {
      ...d.extra_metadata,
      vendor: d.vendor.trim() || null,
      seo_title: d.seo_title.trim() || null,
      seo_description: d.seo_description.trim() || null,
      archived: d.archived || null,
    },
  }
}

function effectiveOptions(d: ProductDraft): DraftOption[] {
  return hasRealOptions(d) ? d.options.filter((o) => o.title.trim() && o.values.length) : [{ title: DEFAULT_OPTION, values: [DEFAULT_VALUE] }]
}

export function validateDraft(d: ProductDraft): string | null {
  if (!d.title.trim()) return "Title can't be blank."
  const opts = d.options.filter((o) => o.title.trim())
  if (new Set(opts.map((o) => o.title.trim().toLowerCase())).size !== opts.length) return "Option names must be different."
  if (hasRealOptions(d)) for (const o of d.options) if (!o.title.trim() || !o.values.length) return `Add a name and at least one value for every option.`
  for (const v of d.variants) {
    if (v.price.trim() === "" || isNaN(Number(v.price)) || Number(v.price) < 0) return `Enter a price for ${variantTitle(v, d)}.`
    if (v.compare_at.trim() && (isNaN(Number(v.compare_at)) || Number(v.compare_at) <= Number(v.price)))
      return `Compare-at price for ${variantTitle(v, d)} must be higher than the price.`
    if (v.cost.trim() && (isNaN(Number(v.cost)) || Number(v.cost) < 0)) return `Cost per item for ${variantTitle(v, d)} must be a positive number.`
    if (v.quantity.trim() && !/^-?\d+$/.test(v.quantity.trim())) return `Quantity for ${variantTitle(v, d)} must be a whole number.`
  }
  const skus = d.variants.map((v) => v.sku.trim()).filter(Boolean)
  if (new Set(skus).size !== skus.length) return "Two variants have the same SKU."
  return null
}

export type SaveResult = { id: string; warnings: string[] }
export type StoreCtx = { salesChannelId?: string; locationId?: string; shippingProfileId?: string }

/** Create or update a product from the draft. Returns warnings for partial steps (e.g. no inventory permission). */
export async function saveProduct(draft: ProductDraft, existing: ProductDraft | null, id: string | null, ctx: StoreCtx): Promise<SaveResult> {
  const warnings: string[] = []
  const [typeId, tags] = await Promise.all([ensureType(draft.type), ensureTags(draft.tags)])
  const base = productPayload(draft, typeId, tags)
  const opts = effectiveOptions(draft)

  let productId = id
  if (!productId) {
    const { product } = await sdk.admin.product.create({
      ...base,
      options: opts.map((o) => ({ title: o.title, values: o.values })),
      variants: draft.variants.map((v) => variantPayload(v, draft)),
      sales_channels: ctx.salesChannelId ? [{ id: ctx.salesChannelId }] : undefined,
      // Like Shopify: every new product ships under the default profile.
      shipping_profile_id: draft.shipping_profile_id ?? ctx.shippingProfileId,
    } as any)
    productId = product.id
  } else {
    // Repair products saved before shipping profiles were assigned.
    const profile = !existing?.shipping_profile_id && ctx.shippingProfileId ? { shipping_profile_id: ctx.shippingProfileId } : {}
    await sdk.admin.product.update(productId, { ...base, ...profile } as any)

    // Options (Medusa 2.17+: options are entities linked to the product).
    // 1) link new options + add values, 2) rename, 3) unlink removed options,
    // 4) move variants, 5) drop values no variant uses any more.
    const before = existing?.options.filter((o) => o.id) ?? []
    const plan: DraftOption[] = opts.map((o) => ({ ...o }))
    if (before.length && !plan.some((o) => o.id)) plan[0] = { ...plan[0], id: before[0].id, value_ids: before[0].value_ids }
    const beforeById = new Map(before.map((o) => [o.id!, o]))
    const linkAdd = plan.filter((o) => !o.id || !beforeById.has(o.id)).map((o) => ({ title: o.title, values: o.values, is_exclusive: true }))
    const linkUpdate = plan
      .filter((o) => o.id && beforeById.has(o.id))
      .map((o) => ({ product_option_id: o.id!, add: o.values.filter((v) => !beforeById.get(o.id!)!.values.includes(v)).map((value) => ({ value })) }))
      .filter((u) => u.add.length)
    if (linkAdd.length || linkUpdate.length) await linkOptions(productId, { add: linkAdd, update: linkUpdate })
    for (const o of plan) {
      const src = o.id ? beforeById.get(o.id) : undefined
      if (src && src.title !== o.title) await sdk.client.fetch(`/admin/product-options/${o.id}`, { method: "POST", body: { title: o.title } })
    }

    // Removing an option: Medusa won't unlink an option that variants still use,
    // nor keep variants missing a value, so variants are rebuilt (prices, SKUs
    // and stock carry over from the draft), as Shopify does.
    const planIds = new Set(plan.map((o) => o.id).filter(Boolean) as string[])
    const remove = before.filter((o) => !planIds.has(o.id!)).map((o) => o.id!)
    const rebuild = remove.length > 0
    const draftVariants = rebuild ? draft.variants.map((v) => ({ ...v, id: undefined, original_quantity: null })) : draft.variants
    if (rebuild) draft = { ...draft, variants: draftVariants }

    // Variants
    const oldIds = new Set(existing?.variants.map((v) => v.id).filter(Boolean) as string[])
    const newIds = new Set(draftVariants.map((v) => v.id).filter(Boolean) as string[])
    const toDelete = [...oldIds].filter((x) => !newIds.has(x))
    const create = draftVariants.filter((v) => !v.id).map((v) => variantPayload(v, draft))
    const update = draftVariants.filter((v) => v.id).map((v) => ({ id: v.id!, ...variantPayload(v, draft) }))
    // Delete first: Medusa validates every remaining variant against the options.
    if (toDelete.length) await sdk.admin.product.batchVariants(productId, { delete: toDelete } as any)
    if (remove.length) await linkOptions(productId, { remove })
    if (create.length || update.length) await sdk.admin.product.batchVariants(productId, { create, update } as any)

    const trim = plan
      .filter((o) => o.id && beforeById.has(o.id))
      .map((o) => {
        const src = beforeById.get(o.id!)!
        return { product_option_id: o.id!, remove: src.values.filter((v) => !o.values.includes(v)).map((v) => src.value_ids?.[v]).filter(Boolean) as string[] }
      })
      .filter((u) => u.remove.length)
    if (trim.length) {
      await linkOptions(productId, { update: trim }).catch((e) => warnings.push(`Variants saved, but old options weren't cleaned up: ${e?.message ?? "error"}`))
    }
  }

  // Stock at the default location for variants whose quantity changed.
  const changed = draft.variants.filter((v) => v.manage_inventory && v.quantity.trim() !== "" && Number(v.quantity) !== v.original_quantity)
  if (changed.length && ctx.locationId) {
    try {
      const { product } = await sdk.admin.product.retrieve(productId, { fields: "variants.id,variants.title,*variants.inventory_items" })
      const items = changed
        .map((dv) => {
          const mv: any =
            (product as any).variants.find((x: any) => x.id === dv.id) ?? (product as any).variants.find((x: any) => x.title === variantTitle(dv, draft))
          const link = mv?.inventory_items?.[0]
          const itemId = link?.inventory_item_id ?? link?.inventory?.id
          return itemId ? { inventory_item_id: itemId as string, stocked_quantity: Number(dv.quantity) } : null
        })
        .filter(Boolean) as { inventory_item_id: string; stocked_quantity: number }[]
      // Through Terify's adjust endpoint so the change shows in inventory history.
      if (items.length) await adjustInventory({ location_id: ctx.locationId, reason: "correction", changes: items })
    } catch (e: any) {
      warnings.push(e?.status === 403 || e?.status === 400 ? "Saved, but stock wasn't updated: you don't have inventory permission." : `Saved, but stock wasn't updated: ${e?.message ?? "error"}`)
    }
  } else if (changed.length) {
    warnings.push("Saved, but stock wasn't updated: no stock location is set up.")
  }

  return { id: productId, warnings }
}

function linkOptions(productId: string, body: { add?: unknown[]; remove?: string[]; update?: unknown[] }) {
  return sdk.client.fetch(`/admin/products/${productId}/options/batch`, { method: "POST", body })
}

// ── Hooks ──────────────────────────────────────────────────────────────────

export function useStoreContext() {
  return useQuery({
    queryKey: ["store-context"],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const [{ stores }, { stock_locations }, profiles] = await Promise.all([
        sdk.admin.store.list({ fields: "id,name,default_sales_channel_id,default_location_id" } as any),
        sdk.admin.stockLocation.list({ limit: 20, fields: "id,name" } as any),
        sdk.admin.shippingProfile.list({ limit: 20, fields: "id,name,type" } as any).catch(() => ({ shipping_profiles: [] as any[] })),
      ])
      const store: any = stores[0]
      return {
        salesChannelId: store?.default_sales_channel_id as string | undefined,
        locationId: (store?.default_location_id as string | undefined) ?? stock_locations[0]?.id,
        locations: stock_locations as { id: string; name: string }[],
        shippingProfileId: ((profiles as any).shipping_profiles.find((p: any) => p.type === "default") ?? (profiles as any).shipping_profiles[0])?.id as string | undefined,
      }
    },
  })
}

export function useProductDraft(id: string | null, locationId?: string) {
  return useQuery({
    queryKey: [...qk.product(id ?? "new"), "draft", locationId],
    enabled: !!id,
    queryFn: async () => {
      const { product } = await sdk.admin.product.retrieve(id!, { fields: PRODUCT_FIELDS })
      return { product, draft: productToDraft(product, locationId) }
    },
  })
}

export function useSaveProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { draft: ProductDraft; existing: ProductDraft | null; id: string | null; ctx: StoreCtx }) =>
      saveProduct(args.draft, args.existing, args.id, args.ctx),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["products"] })
      qc.invalidateQueries({ queryKey: qk.product(r.id) })
    },
  })
}

export function useDeleteProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => sdk.admin.product.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  })
}

export async function uploadFiles(files: File[]): Promise<string[]> {
  if (!files.length) return []
  const { done, failed } = await uploadMany(files)
  if (failed.length) throw new Error(failed.map((f) => `${f.name}: ${f.error}`).join("; "))
  return done.map((f) => f.url)
}

export function useCollections(q?: string) {
  return useQuery({
    queryKey: ["collections", q ?? ""],
    queryFn: () => sdk.admin.productCollection.list({ q: q || undefined, limit: 100, order: "title", fields: "id,title,handle" } as any),
  })
}

export function useProductTags() {
  return useQuery({ queryKey: ["product-tags"], queryFn: () => sdk.admin.productTag.list({ limit: 300, order: "value", fields: "id,value" } as any), staleTime: 60_000 })
}

export function useProductTypes() {
  return useQuery({ queryKey: ["product-types"], queryFn: () => sdk.admin.productType.list({ limit: 200, fields: "id,value" } as any) })
}

/** Duplicate as a draft, Shopify-style "Copy of …". */
export async function duplicateProduct(d: ProductDraft, ctx: StoreCtx) {
  const copy: ProductDraft = {
    ...d,
    title: `Copy of ${d.title}`,
    handle: `${d.handle || slugify(d.title)}-copy-${Math.random().toString(36).slice(2, 6)}`,
    status: "draft",
    archived: false,
    options: d.options.map(({ id: _id, ...o }) => o),
    variants: d.variants.map(({ id: _id, inventory_item_id: _i, original_quantity: _q, ...v }) => ({ ...v, sku: v.sku ? `${v.sku}-COPY` : "", original_quantity: null })),
  }
  return saveProduct(copy, null, null, ctx)
}

/** Duplicate straight from a list row (loads the product first). */
export async function duplicateById(id: string, ctx: StoreCtx) {
  const { product } = await sdk.admin.product.retrieve(id, { fields: PRODUCT_FIELDS })
  return duplicateProduct(productToDraft(product, ctx.locationId), ctx)
}

/** All product ids in list order (title), for the detail page's previous/next arrows. */
export function useProductOrder() {
  return useQuery({
    queryKey: ["products", "order"],
    queryFn: async () => {
      const r: any = await sdk.admin.product.list({ order: "title", limit: 1000, fields: "id" } as any)
      return (r.products as any[]).map((p) => p.id as string)
    },
    staleTime: 60_000,
  })
}

// ── Bulk actions (product list) ─────────────────────────────────────────────

export type BulkAction =
  | { kind: "status"; status: "published" | "draft" }
  | { kind: "archive"; archived: boolean }
  | { kind: "tags"; add?: string[]; remove?: string[] }
  | { kind: "delete" }

/** Applies one action to many products through Medusa's batch endpoint (one request, one audit entry). */
export async function bulkProducts(rows: any[], action: BulkAction) {
  if (!rows.length) return
  if (action.kind === "delete") {
    await sdk.client.fetch("/admin/products/batch", { method: "POST", body: { delete: rows.map((r) => r.id) } })
    return
  }
  let update: any[] = []
  if (action.kind === "status") update = rows.map((r) => ({ id: r.id, status: action.status, metadata: { ...(r.metadata ?? {}), archived: null } }))
  if (action.kind === "archive") update = rows.map((r) => ({ id: r.id, status: "draft", metadata: { ...(r.metadata ?? {}), archived: action.archived ? true : null } }))
  if (action.kind === "tags") {
    const add = await ensureTags(action.add ?? [])
    const removeSet = new Set((action.remove ?? []).map((t) => t.toLowerCase()))
    update = rows.map((r) => {
      const keep = (r.tags ?? []).filter((t: any) => !removeSet.has(String(t.value).toLowerCase())).map((t: any) => ({ id: t.id }))
      const ids = new Set(keep.map((t: any) => t.id))
      return { id: r.id, tags: [...keep, ...add.filter((t) => !ids.has(t.id))] }
    })
  }
  if (action.kind === "archive" && !action.archived) update = update.map((u) => ({ ...u, status: "draft" }))
  await sdk.client.fetch("/admin/products/batch", { method: "POST", body: { update } })
}

export function useBulkProducts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { rows: any[]; action: BulkAction }) => bulkProducts(v.rows, v.action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  })
}
